/**
 * OTP Verification Dialog for Remote CA Signing
 *
 * Flow:
 * 1. User nhập số điện thoại → requestOTP()
 * 2. User nhập mã OTP → verifyOTP()
 * 3. Sau khi verify thành công → callback onVerified()
 */

import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';

interface OTPVerificationDialogProps {
  show: boolean;
  onHide: () => void;
  onVerified: () => void;
  providerName: string;
  requestOTP: (phoneNumber: string) => Promise<{ transactionId: string; otpSent: boolean }>;
  verifyOTP: (otp: string, transactionId: string) => Promise<boolean>;
}

type Step = 'phone' | 'otp' | 'success';

const OTPVerificationDialog: React.FC<OTPVerificationDialogProps> = ({
  show,
  onHide,
  onVerified,
  providerName,
  requestOTP,
  verifyOTP,
}) => {
  const [step, setStep] = useState<Step>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);

  const otpInputRef = useRef<HTMLInputElement>(null);

  // Reset state khi dialog mở
  useEffect(() => {
    if (show) {
      setStep('phone');
      setPhoneNumber('');
      setOtp('');
      setTransactionId('');
      setError('');
      setCountdown(0);
    }
  }, [show]);

  // Countdown timer cho resend OTP
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Focus vào OTP input khi chuyển step
  useEffect(() => {
    if (step === 'otp' && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [step]);

  const handleRequestOTP = async () => {
    if (!phoneNumber.trim()) {
      setError('Vui lòng nhập số điện thoại');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await requestOTP(phoneNumber);
      if (result.otpSent) {
        setTransactionId(result.transactionId);
        setStep('otp');
        setCountdown(60); // 60 giây để resend
      } else {
        setError('Không thể gửi OTP. Vui lòng thử lại.');
      }
    } catch (err: any) {
      setError(err.message || 'Có lỗi xảy ra khi gửi OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp.trim()) {
      setError('Vui lòng nhập mã OTP');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const verified = await verifyOTP(otp, transactionId);
      if (verified) {
        setStep('success');
        setTimeout(() => {
          onVerified();
          onHide();
        }, 1000);
      } else {
        setError('Mã OTP không đúng. Vui lòng thử lại.');
      }
    } catch (err: any) {
      setError(err.message || 'Có lỗi xảy ra khi xác thực OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    await handleRequestOTP();
  };

  const handleKeyPress = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') {
      action();
    }
  };

  const renderPhoneStep = () => (
    <>
      <Modal.Body>
        <div className="text-center mb-4">
          <i className="bi bi-phone fs-1 text-primary"></i>
          <h5 className="mt-2">Xác thực {providerName}</h5>
          <p className="text-muted">
            Nhập số điện thoại đã đăng ký để nhận mã OTP
          </p>
        </div>

        <Form.Group>
          <Form.Label>Số điện thoại</Form.Label>
          <Form.Control
            type="tel"
            placeholder="0912345678"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            onKeyPress={(e) => handleKeyPress(e, handleRequestOTP)}
            disabled={loading}
            autoFocus
          />
        </Form.Group>

        {error && (
          <div className="alert alert-danger mt-3 mb-0 py-2">
            {error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Hủy
        </Button>
        <Button variant="primary" onClick={handleRequestOTP} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Đang gửi...
            </>
          ) : (
            'Gửi mã OTP'
          )}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderOTPStep = () => (
    <>
      <Modal.Body>
        <div className="text-center mb-4">
          <i className="bi bi-shield-lock fs-1 text-primary"></i>
          <h5 className="mt-2">Nhập mã OTP</h5>
          <p className="text-muted">
            Mã OTP đã được gửi đến <strong>{phoneNumber}</strong>
          </p>
        </div>

        <Form.Group>
          <Form.Label>Mã OTP</Form.Label>
          <Form.Control
            ref={otpInputRef}
            type="text"
            placeholder="Nhập mã 6 số"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyPress={(e) => handleKeyPress(e, handleVerifyOTP)}
            disabled={loading}
            maxLength={6}
            className="text-center fs-4 letter-spacing-2"
          />
        </Form.Group>

        <div className="text-center mt-3">
          {countdown > 0 ? (
            <span className="text-muted">
              Gửi lại mã sau {countdown}s
            </span>
          ) : (
            <Button variant="link" onClick={handleResendOTP} disabled={loading}>
              Gửi lại mã OTP
            </Button>
          )}
        </div>

        {error && (
          <div className="alert alert-danger mt-3 mb-0 py-2">
            {error}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={() => setStep('phone')} disabled={loading}>
          Quay lại
        </Button>
        <Button variant="primary" onClick={handleVerifyOTP} disabled={loading || otp.length < 6}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Đang xác thực...
            </>
          ) : (
            'Xác thực'
          )}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccessStep = () => (
    <Modal.Body>
      <div className="text-center py-4">
        <i className="bi bi-check-circle fs-1 text-success"></i>
        <h5 className="mt-2">Xác thực thành công!</h5>
        <p className="text-muted">
          Bạn có thể tiến hành ký số
        </p>
      </div>
    </Modal.Body>
  );

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Xác thực chữ ký số</Modal.Title>
      </Modal.Header>

      {step === 'phone' && renderPhoneStep()}
      {step === 'otp' && renderOTPStep()}
      {step === 'success' && renderSuccessStep()}
    </Modal>
  );
};

export default OTPVerificationDialog;
